<template>
  <div>
    <header class="vm-page-header">
      <div>
        <h1>{{ transferLabel }}</h1>
        <span class="vm-page-header__sub">{{ transferSubLabel }}</span>
      </div>
      <div class="vm-page-header__actions">
        <button class="btn btn--outline btn--sm">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Comprovante
        </button>
      </div>
    </header>

    <div class="fin-body">
      <router-link :to="{ name: 'FinanceiroDashboard' }" class="backlink">
        ‹ Voltar à visão geral
      </router-link>

      <!-- Skeleton -->
      <div v-if="isLoading" class="fdr-grid">
        <div class="stack" style="gap:var(--space-4)">
          <div class="card" style="padding:var(--space-6)">
            <div v-for="i in 4" :key="i" style="margin-bottom:var(--space-3)">
              <div class="vm-skel" style="width:100%;height:16px" />
            </div>
          </div>
          <div class="card" style="padding:var(--space-6)">
            <div class="vm-skel" style="width:100%;height:60px" />
          </div>
        </div>
        <div class="card card--flush">
          <div
            style="padding:var(--space-4);display:flex;flex-direction:column;gap:var(--space-3)"
          >
            <div
              v-for="i in 4"
              :key="i"
              style="display:flex;align-items:center;gap:var(--space-3)"
            >
              <div
                class="vm-skel"
                style="width:36px;height:36px;border-radius:50%"
              />
              <div class="vm-skel" style="flex:1;height:14px" />
              <div class="vm-skel" style="width:80px;height:14px" />
            </div>
          </div>
        </div>
      </div>

      <!-- Conteúdo -->
      <div v-else class="fdr-grid">
        <!-- Coluna esquerda -->
        <div class="stack" style="gap:var(--space-4)">
          <!-- Card: Valor do repasse -->
          <div class="card" style="padding:var(--space-6)">
            <span class="fieldset-title">Valor do repasse</span>
            <div
              class="money money--pos"
              style="font-family:var(--font-num);font-size:var(--font-size-4xl);margin:var(--space-2) 0 var(--space-1)"
            >
              {{ totalLiquidoFormatted }}
            </div>
            <span class="badge" :class="statusBadgeClass">
              {{ statusLabel }}
            </span>

            <dl class="dl" style="margin-top:var(--space-4)">
              <div>
                <dt>Data do repasse</dt>
                <dd class="num">{{ transferDateLabel }}</dd>
              </div>
              <div>
                <dt>Consultas</dt>
                <dd class="num">{{ creditCount }}</dd>
              </div>
              <div>
                <dt>Valor bruto</dt>
                <dd class="num">{{ totalBrutoFormatted }}</dd>
              </div>
              <div>
                <dt>Taxa ({{ platformPercent }}%)</dt>
                <dd class="num" style="color:var(--color-danger)">
                  − {{ totalTaxaFormatted }}
                </dd>
              </div>
            </dl>
          </div>

          <!-- Card: Conta de destino -->
          <div class="card" style="padding:var(--space-6)">
            <span class="fieldset-title">Conta de destino</span>
            <div
              v-if="hasBankAccount"
              class="row"
              style="gap:var(--space-3);margin-top:var(--space-3)"
            >
              <div
                class="avatar avatar--md"
                style="background:var(--color-primary-light);color:var(--color-primary)"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 10l9-6 9 6M5 10v9h14v-9M9 19v-5h6v5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div class="stack" style="gap:1px;flex:1">
                <span
                  style="font-size:var(--font-size-sm);font-weight:var(--fw-bold);color:var(--color-gray-900)"
                >
                  {{ bankAccount.bank_name }}
                </span>
                <span
                  class="num"
                  style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
                >
                  Ag. {{ bankAccount.agency_number }} · CC
                  {{ bankAccount.account_number
                  }}{{
                    bankAccount.account_check_number
                      ? "-" + bankAccount.account_check_number
                      : ""
                  }}
                </span>
              </div>
            </div>
            <div v-else style="margin-top:var(--space-3)">
              <span
                style="font-size:var(--font-size-sm);color:var(--color-gray-400)"
              >
                Nenhuma conta cadastrada
              </span>
            </div>
            <router-link
              :to="{ name: 'FinanceiroConta' }"
              class="btn btn--ghost btn--block btn--sm"
              style="margin-top:var(--space-3);text-decoration:none"
            >
              Ver conta bancária →
            </router-link>
          </div>
        </div>

        <!-- Coluna direita: Consultas incluídas -->
        <div class="card card--flush">
          <div
            class="row"
            style="justify-content:space-between;padding:var(--space-5) var(--space-5) var(--space-3)"
          >
            <h3
              style="font-size:var(--font-size-md);font-weight:var(--fw-bold);color:var(--color-gray-900);margin:0"
            >
              Consultas incluídas
            </h3>
            <span class="badge badge--neutral badge--no-dot">
              {{ creditCount }} itens
            </span>
          </div>

          <div style="overflow-x:auto">
            <table class="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Paciente</th>
                  <th class="num t-right">Bruto</th>
                  <th class="num t-right">Líquido</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(tx, i) in displayedTransactions"
                  :key="i"
                  class="tr--link"
                  @click="goToConsulta(tx, i)"
                >
                  <td class="num" style="white-space:nowrap">
                    <span>{{ datePart(tx.transaction_date) }}</span>
                    <span style="color:var(--color-gray-400)">
                      {{ yearPart(tx.transaction_date) }}
                    </span>
                  </td>
                  <td>
                    <div class="row" style="gap:var(--space-2)">
                      <div class="avatar avatar--sm">
                        {{
                          tx.patient
                            ? tx.patient.substring(0, 2).toUpperCase()
                            : "?"
                        }}
                      </div>
                      <span class="cell-strong">
                        {{ tx.patient || tx.description || "Movimentação" }}
                      </span>
                    </div>
                  </td>
                  <td class="num t-right money">
                    {{ formatBRL(tx.amount / 100) }}
                  </td>
                  <td class="num t-right money money--pos">
                    {{ formatBRL((tx.amount / 100) * doctorFraction) }}
                  </td>
                </tr>
                <tr v-if="!displayedTransactions.length">
                  <td
                    colspan="4"
                    style="text-align:center;color:var(--color-gray-400);font-size:var(--font-size-sm);padding:var(--space-6)"
                  >
                    Nenhuma consulta encontrada
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            style="padding:var(--space-4) var(--space-5);border-top:1px solid var(--color-gray-100);display:flex;align-items:center;justify-content:space-between"
          >
            <router-link
              :to="{ name: 'FinanceiroExtrato' }"
              class="btn btn--ghost btn--sm"
              style="text-decoration:none"
            >
              Ver extrato completo ›
            </router-link>
            <span
              style="font-size:var(--font-size-sm);color:var(--color-gray-500)"
            >
              Total líquido
              <strong class="money money--pos">
                {{ totalLiquidoFormatted }}
              </strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from "date-fns";
import { Doctor } from "../services/doctor";

export default {
  name: "FinanceiroRepasse",

  data() {
    return {
      comissionPercent: 80
    };
  },

  async mounted() {
    await Promise.all([
      this.$store.dispatch("doctor/getFinancials"),
      this.$store.dispatch("doctor/getBankAccount"),
      this.$store.dispatch("doctor/transfers")
    ]);
    Doctor.earnings()
      .then(res => {
        this.comissionPercent = res.data.comission_percent;
      })
      .catch(() => {});
  },

  methods: {
    parseAmount(str) {
      if (!str) return 0;
      const cleaned = String(str)
        .replace(/[R$\s]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
      return parseFloat(cleaned) || 0;
    },
    formatBRL(val) {
      if (!val) return "R$ 0,00";
      const str = val.toFixed(2).replace(".", ",");
      const parts = str.split(",");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return "R$ " + parts.join(",");
    },
    datePart(date) {
      if (!date) return "—";
      try {
        return format(parseISO(date), "dd/MM");
      } catch {
        return "—";
      }
    },
    yearPart(date) {
      if (!date) return "";
      try {
        return "/" + format(parseISO(date), "yy");
      } catch {
        return "";
      }
    },
    formatDate(date) {
      if (!date) return "—";
      try {
        return format(parseISO(date), "dd/MM/yyyy");
      } catch {
        return "—";
      }
    },
    goToConsulta(tx, idx) {
      this.$router.push({
        name: "FinanceiroConsulta",
        params: { id: tx.id ? String(tx.id) : String(idx) }
      });
    }
  },

  computed: {
    doctorFraction() {
      return this.comissionPercent / 100;
    },
    platformFraction() {
      return 1 - this.comissionPercent / 100;
    },
    platformPercent() {
      return 100 - this.comissionPercent;
    },
    isLoading() {
      return this.$store.getters["doctor/loading"];
    },
    currentTransfer() {
      const id = this.$route.params.id;
      return (
        this.$store.state.doctor.transfer_history.find(t => t.id === id) || null
      );
    },
    transactions() {
      return this.$store.state.doctor.financials?.transactions || [];
    },
    creditTransactions() {
      return this.transactions.filter(t => t.type === "crédito");
    },
    displayedTransactions() {
      return this.creditTransactions.slice(0, 6);
    },
    creditCount() {
      return this.creditTransactions.length;
    },
    // Soma dos créditos (financials devolve amount em centavos → reais)
    totalBruto() {
      return this.creditTransactions.reduce(
        (s, t) => s + (t.amount || 0) / 100,
        0
      );
    },
    totalLiquido() {
      return this.totalBruto * this.doctorFraction;
    },
    totalTaxa() {
      return this.totalBruto * this.platformFraction;
    },
    // Valor bruto do repasse: usa o valor real da transferência (centavos) quando
    // disponível; senão, cai na soma dos créditos exibidos.
    transferBrutoReais() {
      if (this.currentTransfer)
        return Number(this.currentTransfer.amount) / 100;
      return this.totalBruto;
    },
    totalBrutoFormatted() {
      return this.formatBRL(this.transferBrutoReais);
    },
    totalLiquidoFormatted() {
      return this.formatBRL(this.transferBrutoReais * this.doctorFraction);
    },
    totalTaxaFormatted() {
      return this.formatBRL(this.transferBrutoReais * this.platformFraction);
    },
    transferStatus() {
      return this.currentTransfer?.status || "pending";
    },
    statusLabel() {
      if (this.transferStatus === "accepted") return "Concluído";
      const date = this.currentTransfer?.created_at;
      return date ? `Agendado para ${this.formatDate(date)}` : "Agendado";
    },
    statusBadgeClass() {
      return this.transferStatus === "accepted"
        ? "badge--success"
        : "badge--warning";
    },
    transferDateLabel() {
      const date = this.currentTransfer?.created_at;
      return date ? this.formatDate(date) : "—";
    },
    bankAccount() {
      return this.$store.state.doctor.bank_account;
    },
    hasBankAccount() {
      return !!this.bankAccount?.id;
    },
    transferLabel() {
      // A API não expõe um número de referência; usamos a data real do repasse.
      const date = this.currentTransfer?.created_at;
      return date ? `Repasse · ${this.formatDate(date)}` : "Repasse";
    },
    transferSubLabel() {
      return `${this.creditCount} consulta${
        this.creditCount !== 1 ? "s" : ""
      } incluída${this.creditCount !== 1 ? "s" : ""}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.fin-body {
  padding: var(--space-6) var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-width: 1000px;
  width: 100%;
}

.fdr-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: var(--space-4);
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.dl {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);

  > div {
    display: flex;
    justify-content: space-between;
  }

  dt {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
  }

  dd {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-800);
    margin: 0;
  }
}
</style>
